/**
 * JobDescriptionParserPage -- AI-powered job description parsing.
 *
 * Users can paste job posting text or enter a URL, then the AI extracts
 * structured data (title, company, skills, keywords, etc.). Parsed results
 * can be used to create a tailored resume site.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Panel } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import {
  useJobDescriptionStore,
  useJobDescriptionActions,
} from '../../state/cms/useJobDescriptionStore';
import type { JobDescription } from '../../types/job-description.types';

/* ------------------------------------------------------------------ */
/*  Internal components                                               */
/* ------------------------------------------------------------------ */

/** Renders a labeled badge for experience level, role type, or work mode. */
function InfoBadge({ label, value }: { label: string; value: string }) {
  return (
    <span className="badge badge--outline" title={label}>
      {value}
    </span>
  );
}

/** Renders a list of skill/keyword tags. */
function TagList({
  title,
  tags,
  variant,
}: {
  title: string;
  tags: string[];
  variant: 'success' | 'active' | 'outline';
}) {
  if (tags.length === 0) return null;

  return (
    <div className="jd-parser__tag-section">
      <h4 className="jd-parser__tag-title">{title}</h4>
      <div className="jd-parser__tags">
        {tags.map((tag, i) => (
          <span key={i} className={`badge badge--${variant}`}>
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Formats a salary range for display. */
function formatSalary(range: { min?: number; max?: number; currency?: string }): string {
  const currency = range.currency ?? 'USD';
  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

  if (range.min != null && range.max != null) return `${fmt(range.min)} - ${fmt(range.max)}`;
  if (range.min != null) return `From ${fmt(range.min)}`;
  if (range.max != null) return `Up to ${fmt(range.max)}`;
  return '';
}

/** Displays the parsed job description results. */
function ParsedResults({ jd }: { jd: JobDescription }) {
  const navigate = useNavigate();
  const { createSiteFromJD } = useJobDescriptionActions();
  const isLoading = useJobDescriptionStore((s) => s.isLoading);

  async function handleCreateSite() {
    const site = await createSiteFromJD(jd.id);
    if (site) {
      navigate(`/cms/sites/${site.id}`);
    }
  }

  return (
    <Panel>
      <Panel.Header>
        <div className="ai-label-row">
          Parsed Results
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreateSite}
            loading={isLoading}
          >
            Create Resume Site
          </Button>
        </div>
      </Panel.Header>
      <Panel.Content>
        {/* Header info */}
        <div className="jd-parser__result-header">
          {jd.job_title && (
            <h3 className="jd-parser__job-title">{jd.job_title}</h3>
          )}
          <div className="jd-parser__meta">
            {jd.company_name && (
              <span className="jd-parser__company">{jd.company_name}</span>
            )}
            {jd.location && (
              <span className="jd-parser__location">{jd.location}</span>
            )}
          </div>
        </div>

        {/* Badges row */}
        {(jd.experience_level || jd.role_type || jd.work_mode) && (
          <div className="jd-parser__badges">
            {jd.experience_level && (
              <InfoBadge label="Experience Level" value={jd.experience_level} />
            )}
            {jd.role_type && (
              <InfoBadge label="Role Type" value={jd.role_type} />
            )}
            {jd.work_mode && (
              <InfoBadge label="Work Mode" value={jd.work_mode} />
            )}
          </div>
        )}

        {/* Salary range */}
        {jd.parsed_data.salary_range &&
          (jd.parsed_data.salary_range.min != null || jd.parsed_data.salary_range.max != null) && (
            <p className="jd-parser__salary">
              <strong>Salary:</strong> {formatSalary(jd.parsed_data.salary_range)}
            </p>
          )}

        {/* Skills and keywords */}
        <TagList title="Required Skills" tags={jd.required_skills} variant="success" />
        <TagList title="Preferred Skills" tags={jd.preferred_skills} variant="active" />
        <TagList title="Keywords" tags={jd.keywords} variant="outline" />

        {/* Summary */}
        {jd.parsed_data.summary && (
          <div className="jd-parser__summary-section">
            <h4 className="jd-parser__tag-title">Summary</h4>
            <p className="jd-parser__summary-text">{jd.parsed_data.summary}</p>
          </div>
        )}
      </Panel.Content>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

type SourceTab = 'text' | 'url';

export function JobDescriptionParserPage() {
  const [activeTab, setActiveTab] = useState<SourceTab>('text');
  const [rawText, setRawText] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');

  const isParsing = useJobDescriptionStore((s) => s.isParsing);
  const error = useJobDescriptionStore((s) => s.error);
  const selectedJD = useJobDescriptionStore((s) => s.selectedJD);
  const { parseJobDescription, clearError, clearSelected } = useJobDescriptionActions();

  const canParse =
    activeTab === 'text'
      ? rawText.trim().length >= 20
      : sourceUrl.trim().length >= 10;

  async function handleParse() {
    clearError();
    clearSelected();

    if (activeTab === 'text') {
      await parseJobDescription({ source_type: 'text', raw_text: rawText.trim() });
    } else {
      await parseJobDescription({ source_type: 'url', source_url: sourceUrl.trim() });
    }
  }

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Parse Job Description</h1>
        <Button
          variant="primary"
          size="md"
          onClick={() => void handleParse()}
          loading={isParsing}
          disabled={!canParse}
        >
          {isParsing ? 'Parsing...' : 'Parse Job Description'}
        </Button>
      </header>

      <Panel>
        <Panel.Header>Job Posting Source</Panel.Header>
        <Panel.Content>
          {/* Tab toggle */}
          <div className="jd-parser__tabs">
            <button
              type="button"
              className={`jd-parser__tab ${activeTab === 'text' ? 'jd-parser__tab--active' : ''}`}
              onClick={() => setActiveTab('text')}
            >
              Paste Text
            </button>
            <button
              type="button"
              className={`jd-parser__tab ${activeTab === 'url' ? 'jd-parser__tab--active' : ''}`}
              onClick={() => setActiveTab('url')}
            >
              Enter URL
            </button>
          </div>

          {/* Text input */}
          {activeTab === 'text' && (
            <div className="input-wrapper">
              <textarea
                className="input input--textarea"
                rows={12}
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder="Paste the job posting here..."
              />
              <span className="jd-parser__char-count">
                {rawText.length} characters
                {rawText.length > 0 && rawText.trim().length < 20 && ' (minimum 20)'}
              </span>
            </div>
          )}

          {/* URL input */}
          {activeTab === 'url' && (
            <div className="input-wrapper">
              <label className="input-label">Job Posting URL</label>
              <input
                className="input"
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          )}
        </Panel.Content>
      </Panel>

      {/* Error display */}
      {error && (
        <div className="jd-parser__error" role="alert">
          <p>{error}</p>
          <Button variant="secondary" size="sm" onClick={clearError}>
            Dismiss
          </Button>
        </div>
      )}

      {/* Parsed results */}
      {selectedJD && selectedJD.status === 'parsed' && (
        <ParsedResults jd={selectedJD} />
      )}

      {/* Parsing failed status */}
      {selectedJD && selectedJD.status === 'failed' && (
        <Panel>
          <Panel.Header>Parsing Failed</Panel.Header>
          <Panel.Content>
            <p className="list-empty">
              {selectedJD.error_message ?? 'The AI was unable to parse this job description. Please try again with different text.'}
            </p>
          </Panel.Content>
        </Panel>
      )}
    </div>
  );
}
